import { T } from 'src/shared/helpers';

// LA GERARCHIA DEL PANNELLO STA QUI, I COMPORTAMENTI RESTANO DOVE SONO. Il pannello di un
// materiale PBR era diciotto gruppi in fila, tutti alla stessa altezza: il colore di base
// accanto al pre-pass, la vernice accanto all'esposizione. La riorganizzazione — che sul
// prototipo vive in MAT_CARDS e MAT_CHAPTERS — dà a quelle proprietà quattro gradini:
//
//     Sezione    di che genere di domanda si tratta   Surface · Shading · Effects · Rendering
//     Gruppo     la domanda                           Appearance, Transparency, Reflection…
//     Canale     la cosa che si tocca                 Base color, Opacity, Reflectance…
//     Parametro  ciò che dipende da quella cosa       Factor, IOR, Min/Max, la sua mappa
//
// QUI NON SI RISCRIVE NIENTE DI CIÒ CHE LE PROPRIETÀ FANNO. Questa tabella **nomina** le
// proprietà che editMaterialsEditorConfig costruisce già — con i loro handler, i loro
// caricamenti di texture, la loro scrittura su Babylon — e le rimette in ordine. È l'unica
// forma che non rischia di cambiare un comportamento mentre sposta una riga, ed è anche il
// motivo per cui quel file non si tocca: lo legge anche il vecchio editor.
//
// UNA PROPRIETÀ CHE QUESTA TABELLA NON NOMINA NON SI VEDE PIÙ. È così che esce dall'interfaccia
// ciò che deve uscire: non c'è una lista dei nascosti da tenere d'accordo con quella dei
// visibili — ce n'è una sola, e dice cosa resta. I valori sul materiale non vengono toccati
// (utente, 2026-08-20; la domanda ancora aperta sta in docs/domande-per-lucio.md § 1).

export interface PbrChannel {
    /** Il nome che si legge. Non ripete quello del gruppo che lo contiene. */
    label: string;
    /** Gli id delle proprietà esistenti, nell'ordine in cui vanno lette. */
    take: string[];
    /**
     * COSA RESTA IN VISTA, e con esso la forma del canale.
     *
     *     un id    quel controllo sta sulla riga, tutto il resto nel cassetto
     *     'map'    sulla riga c'è la sola casella della mappa: il canale non ha un valore da mostrare
     *     assente  il canale resta il gruppo che apre, come oggi
     *
     * L'ASSENZA È UNA DECISIONE, non una dimenticanza: sono i canali dove i valori sono DUE O TRE
     * PARI FRA LORO — occlusione/ruvidità/metallo, reflectance, spessore — e scegliere quale dei
     * tre si vede vuol dire indovinare che cosa serve sott'occhio a chi costruisce un materiale.
     * La domanda è in docs/domande-materiali-per-lucio.md § 3; finché non ha risposta quei canali
     * restano come sono, così non si disfa.
     */
    onRow?: string;
}

export interface PbrGroup {
    id: string;
    label: string;
    /** Nasce aperto. Ce l'hanno i gruppi di Surface: sono le domande di ogni materiale. */
    open?: boolean;
    /** L'interruttore che accende la famiglia: uno strato che un materiale può non avere. */
    feature?: string;
    /**
     * LA TESTATA OSPITA IL COMMUTATORE DEI DUE FLUSSI. Non è un interruttore come `feature` —
     * non accende niente — e per questo è un campo suo: cambia la FORMA di ciò che c'è sotto, e
     * il posto da cui si cambia la forma di una card è la sua testata.
     */
    switcher?: boolean;
    channels: PbrChannel[];
}

export interface PbrSection {
    id: string;
    label: string;
    /** Sta sotto la spina «Advanced». In evidenza resta la sola Surface. */
    advanced?: boolean;
    groups: PbrGroup[];
}

// Uno slot di texture non è una proprietà sola: createTexturePropertyConfig ne produce fino a
// cinque con lo stesso prefisso — la mappa, la sua versione mobile, la scala, il canale UV.
// Nominarle una per una qui vorrebbe dire ricopiare quella funzione; questa le prende tutte.
//
// Scala e canale UV restano DENTRO il canale finché non arriva il mapping del materiale (round
// 2): toglierle adesso toglierebbe una capacità che c'è, per anticipare una che non c'è ancora.
const map = (type: string) => [type + '-image', type + '-texture-scaling', type + '-uv-channel'];

/**
 * I DUE FLUSSI DI CONSEGNA DELLE TRE MAPPE. Non sono due viste della stessa cosa: cambiano
 * quante mappe il materiale ha davvero.
 *
 *     orm       una texture sola, un canale per quantità — R occlusione, G ruvidità, B metallo.
 *               È la convenzione glTF, cioè la forma in cui i modelli arrivano.
 *     separate  tre texture in scala di grigi, una per quantità.
 *
 * LA MODALITÀ NON È UN CAMPO DEL MODELLO: si deduce da com'è fatto il materiale — se ha una
 * `microSurfaceTexture` o una `ambientTexture`, è separate. Un campo in più sarebbe una seconda
 * verità da tenere d'accordo con la prima, e i materiali fatti con il vecchio backoffice si
 * aprirebbero nel flusso sbagliato.
 */
export type FinishMode = 'orm' | 'separate';

export const pbrSections = (finish: FinishMode): PbrSection[] => [
    {
        id: 'surface',
        label: T._('Surface', 'Admin'),
        groups: [
            {
                id: 'appearance',
                label: T._('Appearance', 'Admin'),
                open: true,
                channels: [
                    { label: T._('Base color', 'Admin'), take: ['albedo-color', 'override-albedo-color', ...map('albedo')], onRow: 'albedo-color' },
                    {
                        label: T._('Normal', 'Admin'),
                        take: ['normal-level', ...map('bump'), 'invert-normal-map-X', 'invert-normal-map-Y'],
                        onRow: 'normal-level'
                    }
                ]
            },
            {
                // OCCLUSIONE, RUVIDITÀ E METALLO SONO UNA DOMANDA SOLA — com'è fatta la superficie
                // quando la luce la colpisce. Stanno in una texture sola, un canale per ciascuna:
                // R, G, B. È la convenzione glTF, è come i modelli arrivano, ed è la forma che il
                // foglio di mappatura chiama FINISH [ORM].
                //
                // IL SECONDO FLUSSO — [Separate], con una mappa per ognuna delle tre — NON È QUI, e
                // non per dimenticanza: il foglio lo descrive quasi interamente con righe nuove
                // (uno slot suo per il metallo, i selettori di canale, e per la ruvidità una
                // «logica custom» che isola il verde da microSurface). È sviluppo, non spostamento,
                // e va dove va lo sviluppo — round 2. Finché non c'è, un comando che promettesse i
                // due flussi mentirebbe su metà della promessa.
                id: 'finish',
                label: T._('Finish', 'Admin'),
                open: true,
                switcher: true,
                channels:
                    finish === 'orm'
                        ? [
                              {
                                  label: T._('Occlusion / Roughness / Metallic', 'Admin'),
                                  take: [
                                      'ambient-strength',
                                      'metallic',
                                      'roughness',
                                      ...map('metallic'),
                                      'use-roughness-from-green',
                                      'use-metallness-from-blue',
                                      'use-ambient-occlusion-from-red'
                                  ]
                              }
                          ]
                        : [
                              // UNA RIGA PER QUANTITÀ, e ognuna con la sua mappa nel cassetto. Il valore
                              // resta in vista perché MOLTIPLICA la mappa: a zero la mappa non si vede,
                              // ed è la prima cosa che si va a controllare quando "non funziona".
                              {
                                  label: T._('Occlusion', 'Admin'),
                                  take: ['ambient-strength', ...map('ambient'), 'use-ambient-in-gray-scale'],
                                  onRow: 'ambient-strength'
                              },
                              {
                                  label: T._('Roughness', 'Admin'),
                                  take: ['roughness', ...map('microSurface')],
                                  onRow: 'roughness'
                              },
                              {
                                  // IL METALLO NON HA UNO SLOT SUO IN BABYLON: la mappa va nello stesso
                                  // `metallicTexture` del pacchetto, ma con le tre spunte spente lo shader
                                  // legge il metallo dal ROSSO — che su una mappa in scala di grigi è il
                                  // grigio. È lo «slegato» chiesto, ottenuto spegnendo invece che scrivendo.
                                  label: T._('Metallic', 'Admin'),
                                  take: ['metallic', ...map('metallic')],
                                  onRow: 'metallic'
                              }
                          ]
            },
            {
                id: 'transparency',
                label: T._('Transparency', 'Admin'),
                open: true,
                channels: [
                    // I due interruttori stanno DENTRO il modo e non fra le righe del gruppo:
                    // dicono che cosa il motore fa dell'alfa quando compone la superficie, non
                    // quanto si vede attraverso. Sono del modo, e col modo si spengono.
                    {
                        label: T._('Mode', 'Admin'),
                        take: ['material-transparency-mode', 'useSpecularOverAlpha', 'useRadianceOverAlpha'],
                        onRow: 'material-transparency-mode'
                    },
                    { label: T._('Opacity', 'Admin'), take: ['material-opacity', ...map('opacity')], onRow: 'material-opacity' }
                ]
            }
        ]
    },
    {
        id: 'shading',
        label: T._('Shading', 'Admin'),
        advanced: true,
        groups: [
            {
                // La luce che il materiale PORTA ADDOSSO: l'emissivo è luce che manda fuori, la
                // lightmap luce già calcolata che si appoggia su di lei. Nessuna delle due è luce
                // che riceve — quella sta nei due gruppi qui sotto.
                id: 'illumination',
                label: T._('Illumination', 'Admin'),
                channels: [
                    { label: T._('Emissive', 'Admin'), take: ['emissive-color', ...map('emissive')], onRow: 'emissive-color' },
                    // La lightmap è luce già calcolata: non ha un valore da dosare, ha un file. Sulla riga
                    // resta la sua casella, e la spunta che la usa come ombra sta dentro con l'altro.
                    { label: T._('Lightmap', 'Admin'), take: [...map('lightmap'), 'useLightmapAsShadowmap'], onRow: 'map' }
                ]
            },
            {
                id: 'reflection',
                label: T._('Reflection', 'Admin'),
                channels: [
                    // Il fattore sta dentro perché è l'altra metà della stessa domanda: quanto di
                    // quel colore viene dal metallo.
                    // Reflectance non ha una mappa: il colore che la superficie rimanda allo specchio
                    // è uniforme, e la texture di reflectivity esce dall'interfaccia con tutte le sue.
                    { label: T._('Reflectance', 'Admin'), take: ['metallic-reflectance-color', 'f0-factor'] },
                    { label: T._('Environment', 'Admin'), take: ['reflection-color', ...map('reflection')], onRow: 'reflection-color' }
                ]
            },
            {
                // Due flussi comandano e due righe ubbidiscono: spessore e tinta sono i dati su cui
                // rifrazione e traslucenza lavorano.
                id: 'subsurface',
                label: T._('Subsurface', 'Admin'),
                channels: [
                    {
                        label: T._('Refraction', 'Admin'),
                        take: [
                            'sub-surface-refraction-isRefractionEnabled',
                            'sub-surface-refraction-refractionIntensity',
                            'sub-surface-refraction-indexOfRefraction',
                            'sub-surface-refraction-linkRefractionWithTransparency',
                            'sub-surface-refraction-useAlbedoToTintRefraction',
                            ...map('subSurface_refractionIntensity')
                        ],
                        // SULLA RIGA STA L'INTERRUTTORE, non l'intensità. Un effetto che nasce spento ha una
                        // domanda prima di tutte le altre — c'è o non c'è — e la risposta si legge senza
                        // aprire niente; l'intensità apre il cassetto ed è la prima cosa che ci si trova.
                        onRow: 'sub-surface-refraction-isRefractionEnabled'
                    },
                    {
                        label: T._('Translucency', 'Admin'),
                        take: [
                            'sub-surface-translucency-isTranslucencyEnabled',
                            'sub-surface-translucency-translucencyIntensity',
                            'sub-surface-translucency-diffusionDistance',
                            'sub-surface-translucency-useAlbedoToTintTranslucency',
                            ...map('subSurface_translucencyIntensity')
                        ],
                        onRow: 'sub-surface-translucency-isTranslucencyEnabled'
                    },
                    {
                        // Minimo prima del massimo: due intervalli letti in due versi nello stesso
                        // pannello sono due volte la stessa cosa detta male.
                        label: T._('Thickness', 'Admin'),
                        take: ['sub-surface-thickness-minimumThickness', 'sub-surface-thickness-maximumThickness', ...map('subSurface_thickness')]
                    },
                    {
                        label: T._('Tint', 'Admin'),
                        take: ['sub-surface-tint-tintColor', 'sub-surface-tint-tintColorAtDistance'],
                        onRow: 'sub-surface-tint-tintColor'
                    },
                    // Rifrangere è luce che ATTRAVERSA il materiale: accanto a Reflectance diceva
                    // soltanto «anche questa comincia per environment».
                    { label: T._('Environment', 'Admin'), take: map('refraction'), onRow: 'map' }
                ]
            }
        ]
    },
    {
        id: 'effects',
        label: T._('Effects', 'Admin'),
        advanced: true,
        groups: [
            {
                id: 'clear-coat',
                label: T._('Clear coat', 'Admin'),
                feature: 'clear-coat-is-enabled',
                channels: [
                    { label: T._('Intensity', 'Admin'), take: ['clear-coat-intensity', 'clear-coat-indexOfRefraction'], onRow: 'clear-coat-intensity' },
                    { label: T._('Roughness', 'Admin'), take: ['clear-coat-roughness', ...map('clearCoat_textureRoughness')], onRow: 'clear-coat-roughness' },
                    { label: T._('Bump', 'Admin'), take: map('clearCoat_bump'), onRow: 'map' },
                    {
                        label: T._('Tint', 'Admin'),
                        take: [
                            'clear-coat-tint-isEnabled',
                            'clear-coat-tint-color',
                            'clear-coat-tint-tintColorAtDistance',
                            'clear-coat-tint-tintThickness',
                            ...map('clearCoat_tint')
                        ],
                        onRow: 'clear-coat-tint-isEnabled'
                    }
                ]
            },
            {
                id: 'sheen',
                label: T._('Sheen', 'Admin'),
                feature: 'sheen-isEnabled',
                channels: [
                    { label: T._('Intensity', 'Admin'), take: ['sheen-intensity', 'sheen-albedoScaling', ...map('sheen_texture')], onRow: 'sheen-intensity' },
                    // Il velo di sheen è di un colore solo su tutta la superficie: a variarlo punto
                    // per punto ci pensa già l'intensità, che la texture ce l'ha.
                    { label: T._('Color', 'Admin'), take: ['sheen-color'], onRow: 'sheen-color' }
                ]
            },
            {
                id: 'anisotropy',
                label: T._('Anisotropy', 'Admin'),
                feature: 'anisotropic-isEnabled',
                channels: [
                    { label: T._('Intensity', 'Admin'), take: ['anisotropic-intensity', ...map('anisotropy_texture')], onRow: 'anisotropic-intensity' },
                    // Una direzione e non un angolo: il modello dati è un vettore nel piano della
                    // texture, e un angolo obbligherebbe a convertirlo per mostrarlo e a
                    // riconvertirlo per scriverlo.
                    // RESTA UN GRUPPO perché un vettore sono due campi, e due campi nella colonna di un
                    // valore non ci stanno: si stringerebbero fino a non poterci leggere dentro.
                    { label: T._('Direction', 'Admin'), take: ['anisotropic-direction'] }
                ]
            }
        ]
    },
    {
        id: 'rendering',
        label: T._('Rendering', 'Admin'),
        advanced: true,
        groups: [
            {
                // Il numero per primo e poi gli interruttori: lo z-offset è il solo di questo
                // gruppo che si REGOLA — gli altri si accendono — e un valore in cima a un elenco
                // di interruttori dice meglio che cosa il gruppo governa.
                id: 'options',
                label: T._('Options', 'Admin'),
                channels: [{ label: '', take: ['zOffset', 'twoSidedLighting', 'backFaceCulling', 'needDepthPrePass'] }]
            },
            {
                // Prima la luce che arriva dalle sorgenti e il riflesso che ne nasce — le due metà
                // di una stessa cosa — poi quella che la superficie porta di suo e quella che le
                // arriva dall'intorno. Prima le lampade, poi il resto.
                id: 'light-contribution',
                label: T._('Light contribution', 'Admin'),
                channels: [{ label: '', take: ['direct-intensity', 'specular-intensity', 'emissive-intensity', 'environment-intensity'] }]
            },
            {
                id: 'image-processing',
                label: T._('Image processing', 'Admin'),
                channels: [{ label: '', take: ['camera-exposure', 'camera-contrast'] }]
            }
        ]
    }
];

// I LIMITI CHE IL PANNELLO CONDIVISO SBAGLIA. Non sono ritocchi estetici: l'indice di rifrazione
// nasceva con massimo 1, e con quel tetto il vetro — che sta a 1,5 — non si poteva impostare. Un
// cursore che non arriva dove serve è un campo che non funziona, non un campo scomodo.
//
// Stanno qui e non di là per la stessa ragione delle righe nuove: quel file lo legge anche il
// vecchio editor, e questi limiti sono la correzione decisa per il pannello nuovo.
export const PBR_RANGE_LIMITS: Record<string, { min?: number; max?: number; sliderMax?: number }> = {
    // Da 1 (il vuoto) in su: sotto 1 un indice di rifrazione non esiste.
    'sub-surface-refraction-indexOfRefraction': { min: 1, max: 3 },
    'clear-coat-indexOfRefraction': { min: 1, max: 3 },
    // Lo spessore si regola quasi sempre fra 0 e 10, ma ogni tanto serve di più: il cursore copre
    // l'intervallo utile, il campo numerico non ha tetto.
    'sub-surface-thickness-minimumThickness': { min: 0, sliderMax: 10 },
    'sub-surface-thickness-maximumThickness': { min: 0, sliderMax: 10 }
};
